// useBrainHealth(period) — data hook for the company-wide Brain Health dashboard.
//
// Backed by the real GET /brain/health endpoint (handlers/brain-health.ts), which
// computes every field from a single live index scan + the graph weather store.
// The hook maps that response into the page's view-model (adding per-contributor
// avatar tints, which are a pure presentation concern). The reference `sampleHealth`
// dataset is retained ONLY as a graceful fallback if the endpoint is unreachable.
import { useCallback, useEffect, useState } from "react";
import { getBrainHealth, type BrainHealthResponse } from "./api";

export type Period = "7d" | "30d" | "90d";

export interface BrainHealth {
  period: Period;
  score: number;                       // 0–100 composite
  scoreDelta: number;
  totals: { facts: number; pages: number };
  coveragePct: number; coverageDelta: number;
  openQuestions: number;
  contradictions: { open: number; resolvedAllTime: number };
  series: {
    knowledgeGrowth: number[];
    ingestionPerDay: number[];
    contradictions: { detected: number; resolved: number }[];   // per week
    sparklines: { knowledge: number[]; coverage: number[]; questions: number[]; contradictions: number[] };
  };
  weather: { fresh: number; aging: number; unknown: number; atRisk: number };
  freshness: { under30d: number; d30to90: number; stale: number };
  coverageByArea: { area: string; pct: number }[];
  sources: { name: string; icon: string; status: "ok" | "lagging" | "off"; lastSync?: string }[];
  contributors: { name: string; initials: string; isAgent?: boolean; count: number; bg: string; fg: string }[];
  openContradictions: { title: string; detail: string; severity: "red" | "amber"; ageDays: number }[];
  /** Whether the data came from the live endpoint (true) or the offline sample (false). */
  live: boolean;
}

/** How many sparkline / per-day points a period implies (for shaping sample series). */
const PERIOD_DAYS: Record<Period, number> = { "7d": 7, "30d": 30, "90d": 90 };

/** Stable per-name avatar colours (presentation-only; the endpoint sends no colours). */
const AVATAR_TINTS: { bg: string; fg: string }[] = [
  { bg: "#e3d2c9", fg: "#8a5a44" }, { bg: "#cfeceb", fg: "#0a7d7f" },
  { bg: "#ece2c4", fg: "#8a6d1f" }, { bg: "#d7dde8", fg: "#5c6b85" },
  { bg: "#dccfe6", fg: "#6b4b8a" }, { bg: "#cfe6d6", fg: "#3a7d5a" },
];
function tintFor(name: string): { bg: string; fg: string } {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}

/** Map the endpoint response into the page view-model (adds contributor tints). */
function fromResponse(r: BrainHealthResponse, period: Period): BrainHealth {
  return {
    period,
    score: r.score,
    scoreDelta: r.scoreDelta,
    totals: r.totals,
    coveragePct: r.coveragePct,
    coverageDelta: r.coverageDelta,
    openQuestions: r.openQuestions,
    contradictions: r.contradictions,
    series: r.series,
    weather: r.weather,
    freshness: r.freshness,
    coverageByArea: r.coverageByArea,
    sources: r.sources,
    contributors: r.contributors.map((c) => ({
      ...c,
      bg: c.isAgent ? "var(--color-text-primary)" : tintFor(c.name).bg,
      fg: c.isAgent ? "#fff" : tintFor(c.name).fg,
    })),
    openContradictions: r.openContradictions,
    live: true,
  };
}

/** Offline fallback only — used if GET /brain/health is unreachable. */
function sampleHealth(period: Period): BrainHealth {
  const days = PERIOD_DAYS[period];
  const ingestSeed = [62, 48, 71, 90, 66, 54, 38, 80, 95, 72, 58, 84, 112, 78];
  const ingestionPerDay = Array.from({ length: days }, (_, i) => ingestSeed[i % ingestSeed.length]);
  return {
    period,
    score: 78, scoreDelta: 3,
    totals: { facts: 7420, pages: 1204 },
    coveragePct: 92, coverageDelta: 2,
    openQuestions: 9,
    contradictions: { open: 12, resolvedAllTime: 184 },
    series: {
      knowledgeGrowth: [5200, 5380, 5520, 5610, 5790, 5980, 6130, 6320, 6520, 6680, 6840, 7010, 7180, 7300, 7420],
      ingestionPerDay,
      contradictions: [[6, 4], [8, 7], [5, 6], [9, 8], [7, 9], [10, 8], [6, 9], [5, 7]].map(([detected, resolved]) => ({ detected, resolved })),
      sparklines: {
        knowledge: [5200, 5400, 5700, 5950, 6100, 6400, 6700, 7000, 7240, 7420],
        coverage: [84, 85, 86, 88, 89, 90, 90, 91, 92],
        questions: [14, 13, 15, 12, 11, 12, 10, 9],
        contradictions: [22, 19, 20, 16, 15, 14, 13, 12],
      },
    },
    weather: { fresh: 980, aging: 148, unknown: 64, atRisk: 12 },
    freshness: { under30d: 690, d30to90: 320, stale: 194 },
    coverageByArea: [
      { area: "Company", pct: 96 }, { area: "Dev", pct: 91 }, { area: "DevOps", pct: 88 },
      { area: "Ops", pct: 74 }, { area: "Support", pct: 69 }, { area: "Sales", pct: 52 },
    ],
    sources: [
      { name: "Slack", icon: "brand-slack", status: "ok", lastSync: "synced 2m ago" },
      { name: "Jira", icon: "ticket", status: "lagging", lastSync: "lagging · 3h behind" },
    ],
    contributors: [
      { name: "Aeronet Brain", initials: "", isAgent: true, count: 1240, bg: "var(--color-text-primary)", fg: "#fff" },
      { name: "Isaac M", initials: "IM", count: 318, ...tintFor("Isaac M") },
      { name: "Asha S", initials: "AS", count: 264, ...tintFor("Asha S") },
    ],
    openContradictions: [
      { title: "Brain Pipeline Plan vs Aeronet (internal)", detail: "sync cadence — nightly vs hourly", severity: "red", ageDays: 2 },
      { title: "Deploy runbook lists two owners", detail: "DevOps · conflicting ownership", severity: "red", ageDays: 4 },
    ],
    live: false,
  };
}

export function useBrainHealth(period: Period): { data: BrainHealth; loading: boolean; reload: () => void } {
  const [data, setData] = useState<BrainHealth>(() => sampleHealth(period));
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getBrainHealth(period);
      setData(fromResponse(r, period));
    } catch {
      // Endpoint unreachable — fall back to the offline reference sample.
      setData(sampleHealth(period));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { void load(); }, [load]);

  return { data, loading, reload: () => void load() };
}
